import "../styling/App.css";
import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../authSlice';

// Import all your page/component files
import BookABooth from "./BookABooth";
import Views from "./Views";
import BookABooth2 from "./BookABooth2";
import Login from "./Login";
import Password from "./Password";
import SignUp from "./SignUp";
import Membership from "../components/payments/Membership";
import StripePayment from "../components/payments/StripePayment";
import ClientPortal from "./clientPortal/ClientPortal";
import ClientLogin from "./payments/ClientLogin";
import ClientContainer from "./clientPortal/ClientContainer";
import BookABoothLink from "./BookABoothLink";
import ClientEmailVerify from "./clientPortal/ClientEmailVerify";
import { EventDataProvider } from "./EventDataContext";
import CheckVerification from "./CheckVerification";
import CheckMail from "./CheckMail";
import BusinessClientContainer from "./BusinessClientContainer";
import BusinessEmailVerify from "./BusinessEmailVerify";
import VerificationCode from "./VerificationCode";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import ForgotClientPassword from "./ForgotClientPassword";
import axios from "axios";
import PaymentSuccess from './PaymentSuccess';


// ==================================================================
// --- THIS COMPONENT IS UPDATED ---
// ==================================================================
const CatchAllRedirect = () => {
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  const searchParams = new URLSearchParams(location.search);
  const hasBusinessIdInUrl = searchParams.has("businessId");
  
  // Check if the current URL suggests we are in the Client ecosystem
  const isClientPath = location.pathname.startsWith('/client');

  const publicOnlyPaths = ['/client-login', '/login', '/signup'];

  if (isLoggedIn && publicOnlyPaths.includes(location.pathname)) {
    if (hasBusinessIdInUrl) return null;
    const redirectTo = user?.role === 'Client' ? "/client-home" : "/";
    return <Navigate to={redirectTo} replace />;
  }

  // LOGGED OUT REDIRECT LOGIC
  if (!isLoggedIn && !publicOnlyPaths.includes(location.pathname)) {
    // If we have a businessId OR we are on a client-specific path, go to client-login
    if (hasBusinessIdInUrl || isClientPath) {
      return <Navigate to={`/client-login${location.search}`} replace />;
    }
    // Default for everyone else
    return <Navigate to="/login" replace />;
  }
  
  return null;
};

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const user = useSelector((state) => state.auth.user);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  // This useEffect is still useful for the specific logic of handling `sessionStorage`.
  useEffect(() => {
    if (isLoggedIn && user?.role === 'Client') {
      const loginBusinessId = sessionStorage.getItem('loginBusinessId');
      if (loginBusinessId) {
        navigate(`/client-home?businessId=${loginBusinessId}`, { replace: true });
        sessionStorage.removeItem('loginBusinessId');
      }
    }
  }, [isLoggedIn, user, navigate]); // Removed location dependency as CatchAllRedirect now handles that logic

  // UI state for business portal
  const [selectedMenuItem, setSelectedMenuItem] = useState("Dashboard");

  // Centralized logout handler
  const handleLogout = async () => {
    try {
      // 1. Call the Business Logout API
      await axios.post(
        `${process.env.REACT_APP_API_URL}/business-user/logout`,
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error("API logout failed, clearing local data anyway:", error);
    } finally {
      // 2. Wipe all browser storage
      localStorage.clear();
      sessionStorage.clear();

      // 3. Clear Redux state
      dispatch(logout());

      // 4. Force navigation to the main business login
      navigate("/login", { replace: true });
    }
  };

  // Other UI handlers for business portal
  const handleMenuItemClick = (menuItem) => setSelectedMenuItem(menuItem.text);
  const onEventClick = () => setSelectedMenuItem("businessEvent");
  const rightPaneEventClick = () => setSelectedMenuItem("Bookings");
  const onPanelEventClick = () => setSelectedMenuItem("businessEvent");
  const handleDropdownClick = (account) => {
    setSelectedMenuItem(account);
  };

  return (
    <div className="appContain">
      <EventDataProvider>
        <Routes>
          {
            <Route path="/verify-business-email" element={<BusinessEmailVerify />} />
          }
          {!isLoggedIn && (
            <>
              <Route path="/login" element={<Login />} />
              <Route path="/client-login" element={<ClientLogin />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgotPassword" element={<ForgotPassword />} />
              <Route path="/forgotClientPassword" element={<ForgotClientPassword />} />
              <Route path="/resetPassword" element={<ResetPassword />} />
              <Route path="/checkMail" element={<CheckMail />} />
              <Route path="/checkCode" element={<VerificationCode />} />
              
              <Route path="/book-a-booth" element={<BookABoothLink />} />
              <Route path="/password" element={<Password />} />

              <Route path="/payment" element={<StripePayment />} />

              <Route path="/check-verification" element={<CheckVerification />} />
              <Route path="/verify-email" element={<ClientEmailVerify />} />
            </>
          )}

          {/* LOGGED IN USER LOGIC */}
          {isLoggedIn && user && (
            <>
              {/* NOT VERIFIED */}
              {!user.verified && (
                <>
                  <Route path="/check-verification" element={<CheckVerification />} />
                  <Route path="*" element={<Navigate to="/check-verification" replace />} />
                </>
              )}

              {/* VERIFIED */}
              {user.verified && (
                <>
                  {/* BUSINESS ROUTES */}
                  {user.role === 'business' && (
                    <>
                      <Route 
                        path="/" 
                        element={<Views 
                          handleLogout={handleLogout} 
                          selectedMenuItem={selectedMenuItem} 
                          setSelectedMenuItem={setSelectedMenuItem} 
                          handleMenuItemClick={handleMenuItemClick}
                          onEventClick={onEventClick}
                          rightPaneEventClick={rightPaneEventClick}
                          onPanelEventClick={onPanelEventClick}
                          handleDropdownClick={handleDropdownClick}
                        />} 
                      />
                      
                      <Route path="/membership" element={<Membership />} />
                      <Route path="/client/:id" element={<BusinessClientContainer />} />
                      <Route path="/payment" element={<StripePayment />} />
                    </>
                  )}

                  {/* CLIENT ROUTES */}
                  {user.role === 'Client' && (
                    <>
                      <Route path="/client-home" element={<ClientContainer />} />
                      <Route path="/client-portal" element={<ClientPortal />} />
                      <Route path="/booth2" element={<BookABooth2 />} />
                      <Route path="/booth" element={<BookABooth />} />
                      <Route path="/payment" element={<StripePayment />} />
                      <Route path="/payment-success" element={<PaymentSuccess />} />
                    </>
                  )}
                </>
              )}
            </>
          )}
          
          {/* FINAL REDIRECT LOGIC */}
          <Route path="*" element={<CatchAllRedirect />} />
        </Routes>
      </EventDataProvider>
    </div>
  );
}

export default App;